import React from 'react'
import { Github, Twitter } from 'lucide-react'
import neoserveLogo from '../../assets/neoserve_logo.png'
import { Hero } from '@/components/landing/Hero'
import { Features } from '@/components/landing/Features'
import { PricingComparison } from '@/components/landing/PricingComparison'
import { WhyLocal } from '@/components/landing/WhyLocal'
import { ModelsAndUseCases } from '@/components/landing/ModelsAndUseCases'
import { ModelCatalog } from '@/components/landing/ModelCatalog'
import { WaitlistForm } from '@/components/landing/WaitlistForm'
import { Button } from '@/components/ui/button'
import { ScrollReveal } from '@/components/landing/ScrollReveal'
import { smoothScrollToId } from '@/lib/smoothScroll'

function Navbar() {
  return (
    <nav className="sticky top-0 z-40 bg-[#050810]/80 backdrop-blur-md border-b border-white/[0.06]">
      <div className="container">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <img src={neoserveLogo} alt="Neoserve" className="h-8 w-auto" />
          </div>

          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={() => smoothScrollToId('waitlist', 80)}
              className="bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/20"
            >
              Get Access
            </Button>
          </div>
        </div>
      </div>
    </nav>
  )
}

function Footer() {
  return (
    <footer className="bg-[#030508] border-t border-white/[0.06] text-gray-500 py-12">
      <div className="container">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <img src={neoserveLogo} alt="Neoserve" className="h-7 w-auto" />
          </div>
          <p className="text-sm leading-relaxed max-w-xs text-gray-500">
            The AI API platform built for Asia-Pacific developers and enterprises.
            OpenAI-compatible, APAC-hosted.
          </p>
        </div>

        <div className="border-t border-white/[0.06] pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-600">
            &copy; {new Date().getFullYear()} Neoserve. All rights reserved. Hosted on the edge.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:text-white transition-colors">
              <Github className="h-4 w-4" />
            </a>
            <a href="#" className="hover:text-white transition-colors">
              <Twitter className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default function LandingPage() {
  return (
    <div className="landing min-h-screen flex flex-col bg-[#050810]">
      <Navbar />
      <main className="flex-1">
        <Hero />
        <Features />
        <PricingComparison />
        <WhyLocal />
        <ModelsAndUseCases />
        <ModelCatalog />

        {/* Waitlist Section */}
        <section id="waitlist" className="py-24 bg-[#07090f] relative overflow-hidden">
          {/* Glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-indigo-600/10 rounded-full blur-[100px]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-violet-600/8 rounded-full blur-[80px]" />
          </div>

          {/* Grid overlay */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.025]"
            style={{
              backgroundImage: 'linear-gradient(rgba(148,163,184,1) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,1) 1px, transparent 1px)',
              backgroundSize: '50px 50px',
            }}
          />

          <div className="container relative z-10">
            <div className="max-w-lg mx-auto">
              <ScrollReveal className="text-center mb-10">
                <h2 className="text-3xl font-bold text-white mb-3">
                  Get Early Access
                </h2>
                <p className="text-gray-400">
                  Be among the first APAC developers to get access to Neoserve.
                  We'll reach out when we're ready in your region.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={150} className="animate-float">
                {/* Pulsing glow behind the card */}
                <div className="absolute inset-0 -z-10 rounded-2xl bg-indigo-600/8 blur-2xl scale-95 animate-float-slow" />

                <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 shadow-2xl shadow-indigo-500/10">
                  {/* Top shine */}
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-500/40 to-transparent rounded-t-2xl" />
                  {/* Bottom edge glow */}
                  <div className="absolute inset-x-8 bottom-0 h-px bg-gradient-to-r from-transparent via-violet-500/30 to-transparent" />
                  <WaitlistForm />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
